import React, { Suspense, lazy } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import AdminLayout from "@/components/AdminLayout";

const Home = lazy(() => import("@/pages/Home"));
const Casinos = lazy(() => import("@/pages/Casinos"));
const CasinoDetail = lazy(() => import("@/pages/CasinoDetail"));
const Bookmakers = lazy(() => import("@/pages/Bookmakers"));
const BookmakerDetail = lazy(() => import("@/pages/BookmakerDetail"));
const Bonuses = lazy(() => import("@/pages/Bonuses"));
const Calculators = lazy(() => import("@/pages/Calculators"));
const CalculatorDetail = lazy(() => import("@/pages/CalculatorDetail"));
const Strategies = lazy(() => import("@/pages/Strategies"));
const StrategyDetail = lazy(() => import("@/pages/StrategyDetail"));
const Academy = lazy(() => import("@/pages/Academy"));
const AcademySport = lazy(() => import("@/pages/AcademySport"));
const AcademyCasino = lazy(() => import("@/pages/AcademyCasino"));
const AcademyLesson = lazy(() => import("@/pages/AcademyLesson"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const GlossarySport = lazy(() => import("@/pages/GlossarySport"));
const GlossaryCasino = lazy(() => import("@/pages/GlossaryCasino"));
const Sports = lazy(() => import("@/pages/Sports"));
const SportDetail = lazy(() => import("@/pages/SportDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Providers = lazy(() => import("@/pages/Providers"));
const PaymentMethods = lazy(() => import("@/pages/PaymentMethods"));
const ResponsibleGambling = lazy(() => import("@/pages/ResponsibleGambling"));
const SafetyLicenses = lazy(() => import("@/pages/SafetyLicenses"));
const Quiz = lazy(() => import("@/pages/Quiz"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const LegalPages = lazy(() => import("@/pages/LegalPages"));
const PromoCodesPage = lazy(() => import("@/pages/PromoCodesPage"));
const Compare = lazy(() => import("@/pages/Compare"));
const Tag = lazy(() => import("@/pages/Tag"));
const BlogCategoryHub = lazy(() => import("@/pages/BlogCategoryHub"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Brand = lazy(() => import("@/pages/Brand"));
const Alternativak = lazy(() => import("@/pages/Alternativak"));
const CompetitorKiller = lazy(() => import("@/pages/CompetitorKiller"));
const SlotHub = lazy(() => import("@/pages/SlotHub"));
const PaymentMethodLanding = lazy(() => import("@/pages/PaymentMethodLanding"));
const PromoCodeCasinoLanding = lazy(() => import("@/pages/PromoCodeCasinoLanding"));
const BettingTypeLanding = lazy(() => import("@/pages/BettingTypeLanding"));
const BankrollManagement = lazy(() => import("@/pages/BankrollManagement"));
const Games = lazy(() => import("@/pages/Games"));
const GameDetail = lazy(() => import("@/pages/GameDetail"));

const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminCasinos = lazy(() => import("@/pages/admin/AdminCasinos"));
const AdminBookmakers = lazy(() => import("@/pages/admin/AdminBookmakers"));
const AdminBlog = lazy(() => import("@/pages/admin/AdminBlog"));
const AdminSubscribers = lazy(() => import("@/pages/admin/AdminSubscribers"));
const AdminClicks = lazy(() => import("@/pages/admin/AdminClicks"));
const AdminPromoCodes = lazy(() => import("@/pages/admin/AdminPromoCodes"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminBugReports = lazy(() => import("@/pages/admin/AdminBugReports"));
const AdminContact = lazy(() => import("@/pages/admin/AdminContact"));

function Loader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="text-gold font-display text-2xl animate-pulse">Bonuszvadász...</div>
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Public site */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/kaszinok" element={<Casinos />} />
              <Route path="/kaszinok/:slug" element={<CasinoDetail />} />
              <Route path="/uj-kaszinok" element={<Casinos preset="new" />} />
              <Route path="/elo-kaszino" element={<Casinos preset="live" />} />
              <Route path="/crypto-kaszino" element={<Casinos preset="crypto" />} />
              <Route path="/huf-kaszinok" element={<Casinos preset="huf" />} />
              <Route path="/befizetes-nelkuli-bonusz" element={<Casinos preset="no_deposit" />} />
              <Route path="/ingyenes-porgetesek" element={<Casinos preset="fs" />} />
              <Route path="/mobil-kaszino" element={<Casinos preset="mobile" />} />

              <Route path="/sportfogadas" element={<Bookmakers />} />
              <Route path="/sportfogadas/:slug" element={<BookmakerDetail />} />
              <Route path="/uj-bukmekerek" element={<Bookmakers preset="new" />} />
              <Route path="/gyors-kifizetes-bukmekerek" element={<Bookmakers preset="fast" />} />
              <Route path="/live-fogadas" element={<Bookmakers preset="live" />} />
              <Route path="/crypto-bukmekerek" element={<Bookmakers preset="crypto" />} />
              <Route path="/bajnokok-ligaja" element={<SportDetail slug="bajnokok-ligaja" override />} />

              <Route path="/bonuszok" element={<Bonuses />} />
              <Route path="/bonuszok/:type" element={<Bonuses />} />
              <Route path="/promocios-kodok" element={<PromoCodesPage />} />

              <Route path="/kalkulatorok" element={<Calculators />} />
              <Route path="/kalkulatorok/wagering-kalkulator" element={<Navigate to="/kalkulatorok/wagering" replace />} />
              <Route path="/kalkulatorok/martingale-szimulator" element={<Navigate to="/kalkulatorok/martingale" replace />} />
              <Route path="/kalkulatorok/:slug" element={<CalculatorDetail />} />

              <Route path="/strategiak" element={<Strategies />} />
              <Route path="/strategiak/:slug" element={<StrategyDetail />} />

              <Route path="/akademia" element={<Academy />} />
              <Route path="/akademia/sportfogadas" element={<AcademySport />} />
              <Route path="/akademia/sportfogadas/:slug" element={<AcademyLesson type="sport" />} />
              <Route path="/akademia/kaszino" element={<AcademyCasino />} />
              <Route path="/akademia/kaszino/:slug" element={<AcademyLesson type="casino" />} />

              <Route path="/szakszotar" element={<Glossary />} />
              <Route path="/szakszotar/sport" element={<GlossarySport />} />
              <Route path="/szakszotar/kaszino" element={<GlossaryCasino />} />

              <Route path="/sportok" element={<Sports />} />
              <Route path="/sportok/:slug" element={<SportDetail />} />

              <Route path="/jatek-szolgaltatok" element={<Providers />} />
              <Route path="/jatekok" element={<Games />} />
              <Route path="/jatekok/:slug" element={<GameDetail />} />
              <Route path="/fizetesi-modok" element={<PaymentMethods />} />
              <Route path="/biztonsag-licencek" element={<SafetyLicenses />} />
              <Route path="/felelos-jatek" element={<ResponsibleGambling />} />

              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/oldal/:page" element={<Blog />} />
              <Route path="/blog/kategoria/:category" element={<BlogCategoryHub />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/tag/:tag" element={<Tag />} />

              <Route path="/melyik-a-tokeletes-kaszino" element={<Quiz />} />
              <Route path="/osszehasonlitas" element={<Compare />} />

              {/* SEO landing oldalak — kulcsszó-fókuszú új URL-ek */}
              <Route path="/alternativak/:slug" element={<Alternativak />} />
              <Route path="/tippmix-helyett" element={<CompetitorKiller slugOverride="tippmix-helyett" />} />
              <Route path="/tippmixpro-helyett" element={<CompetitorKiller slugOverride="tippmixpro-helyett" />} />
              <Route path="/vegas-hu-helyett" element={<CompetitorKiller slugOverride="vegas-hu-helyett" />} />
              <Route path="/unibet-helyett" element={<CompetitorKiller slugOverride="unibet-helyett" />} />
              <Route path="/bet365-helyett" element={<CompetitorKiller slugOverride="bet365-helyett" />} />
              <Route path="/aviator" element={<SlotHub />} />
              <Route path="/sweet-bonanza" element={<SlotHub />} />
              <Route path="/gates-of-olympus" element={<SlotHub />} />
              <Route path="/jetx" element={<SlotHub />} />
              <Route path="/book-of-ra" element={<SlotHub />} />
              <Route path="/sizzling-hot" element={<SlotHub />} />
              <Route path="/fizetesi-modok/:method" element={<PaymentMethodLanding />} />
              <Route path="/brand" element={<Brand />} />

              {/* SEO landing oldalak — kaszinó-specifikus promóciós kódok (P1) */}
              <Route path="/promocios-kodok/:slug" element={<PromoCodeCasinoLanding />} />

              {/* SEO landing oldalak — fogadás típusok (Heinz, Yankee, Canadian, Banker, Patent, Trixie, Lucky15) */}
              <Route path="/fogadas-tipusok/:type" element={<BettingTypeLanding />} />

              {/* SEO landing oldal — bankroll management (P2) */}
              <Route path="/bankroll-management" element={<BankrollManagement />} />

              {/* SEO redirektek — törölt konkurens-killer URL-ek → partner kaszinó oldalra (301-szerű) */}
              <Route path="/vulkan-vegas-helyett" element={<Navigate to="/kaszinok/vvegas" replace />} />
              <Route path="/ice-casino-helyett" element={<Navigate to="/kaszinok/icecasino" replace />} />

              {/* SEO redirektek — törölt partnerek (Qzino, NV Casino, BetAndYou, Fireball Casino) */}
              <Route path="/kaszinok/qzino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/kaszinok/nv-casino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/kaszinok/betandyou" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/kaszinok/fireball-casino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/sportfogadas/betandyou" element={<Navigate to="/sportfogadas" replace />} />
              <Route path="/alternativak/qzino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/alternativak/nv-casino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/alternativak/betandyou" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/alternativak/fireball-casino" element={<Navigate to="/kaszinok" replace />} />
              <Route path="/blog/betandyou-kaszino-velemeny-2026" element={<Navigate to="/blog" replace />} />
              <Route path="/blog/betandyou-bonusz-2026-igenyles" element={<Navigate to="/blog" replace />} />
              <Route path="/blog/qzino-kaszino-velemeny-2026" element={<Navigate to="/blog" replace />} />
              <Route path="/blog/qzino-bonusz-2026-igenyles" element={<Navigate to="/blog" replace />} />
              <Route path="/blog/csak-crypto-kaszino-jelentese-elonyei" element={<Navigate to="/blog" replace />} />
              <Route path="/blog/nv-casino-80-free-spin-hell-hot-100" element={<Navigate to="/blog" replace />} />

              <Route path="/rolunk" element={<About />} />
              <Route path="/kapcsolat" element={<Contact />} />
              <Route path="/adatvedelem" element={<LegalPages page="privacy" />} />
              <Route path="/aszf" element={<LegalPages page="terms" />} />
              <Route path="/cookie-iranyelvek" element={<LegalPages page="cookies" />} />
              <Route path="/affiliate-kozlemeny" element={<LegalPages page="affiliate" />} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/kaszinok" element={<AdminCasinos />} />
              <Route path="/admin/bukmekerek" element={<AdminBookmakers />} />
              <Route path="/admin/blog" element={<AdminBlog />} />
              <Route path="/admin/feliratkozok" element={<AdminSubscribers />} />
              <Route path="/admin/kattintasok" element={<AdminClicks />} />
              <Route path="/admin/promo-kodok" element={<AdminPromoCodes />} />
              <Route path="/admin/velemenyek" element={<AdminReviews />} />
              <Route path="/admin/hibajelentesek" element={<AdminBugReports />} />
              <Route path="/admin/uzenetek" element={<AdminContact />} />
              <Route path="/admin/casinos" element={<Navigate to="/admin/kaszinok" replace />} />
              <Route path="/admin/bookmakers" element={<Navigate to="/admin/bukmekerek" replace />} />
              <Route path="/admin/promo-codes" element={<Navigate to="/admin/promo-kodok" replace />} />
              <Route path="/admin/push-to-production" element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="/admin/subscribers" element={<Navigate to="/admin/feliratkozok" replace />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </div>
  );
}

export default App;
